import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VistaCalcLogo from './VistaCalcLogo';
import { investmentAPI } from '../services/api';

interface InvestmentFormData {
  property_price_eur: number;
  monthly_rent_eur: number;
  monthly_expenses_eur: number;
  annual_appreciation_pct: number;
  holding_period_years: number;
  purchase_costs_eur: number;
  selling_costs_eur: number;
}

const InvestmentCalculator: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<InvestmentFormData>({
    property_price_eur: 500000,
    monthly_rent_eur: 2500,
    monthly_expenses_eur: 800,
    annual_appreciation_pct: 3,
    holding_period_years: 10,
    purchase_costs_eur: 15000,
    selling_costs_eur: 20000,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('rate') || name.includes('percent') ? parseFloat(value) || 0 : parseFloat(value) || 0
    }));
  };

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      console.log('Sending investment data:', formData);
      const response = await investmentAPI.calculateInvestment(formData);
      console.log('Investment API response:', response);
      setResults(response);
    } catch (err: any) {
      console.error('Investment calculation error:', err);
      setError(err.message || 'An error occurred while calculating investment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-vista-silver-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-vista-silver-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-vista-silver-600 hover:text-vista-teal-500 transition-colors"
              >
                ← Back to Home
              </button>
              <VistaCalcLogo size="sm" />
            </div>
            <h1 className="text-xl font-bold text-vista-silver-900">Investment Calculator</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Inputs */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-vista-silver-200">
            <h2 className="text-xl font-bold text-vista-silver-900 mb-6">Investment Parameters</h2>
            
            <div className="space-y-6">
              {/* Property Price */}
              <div>
                <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                  Property Price (€)
                </label>
                <input
                  type="number"
                  name="property_price_eur"
                  value={formData.property_price_eur || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                  placeholder="500000"
                />
              </div>

              {/* Rental Income and Expenses */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Monthly Rent (€)
                  </label>
                  <input
                    type="number"
                    name="monthly_rent_eur"
                    value={formData.monthly_rent_eur || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="2500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Monthly Expenses (€)
                  </label>
                  <input
                    type="number"
                    name="monthly_expenses_eur"
                    value={formData.monthly_expenses_eur || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="800"
                  />
                </div>
              </div>

              {/* Appreciation and Holding Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Annual Appreciation (%)
                  </label>
                  <input
                    type="number"
                    name="annual_appreciation_pct"
                    value={formData.annual_appreciation_pct || ''}
                    onChange={handleInputChange}
                    step="0.1"
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="3.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Holding Period (Years)
                  </label>
                  <input
                    type="number"
                    name="holding_period_years"
                    value={formData.holding_period_years || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="10"
                  />
                </div>
              </div>

              {/* Costs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Purchase Costs (€)
                  </label>
                  <input
                    type="number"
                    name="purchase_costs_eur"
                    value={formData.purchase_costs_eur || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="15000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vista-silver-700 mb-2">
                    Selling Costs (€)
                  </label>
                  <input
                    type="number"
                    name="selling_costs_eur"
                    value={formData.selling_costs_eur || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-vista-silver-300 rounded-xl focus:ring-2 focus:ring-vista-teal-500 focus:border-vista-teal-500 transition-colors"
                    placeholder="20000"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={loading}
                className="w-full bg-gradient-to-r from-vista-teal-500 to-vista-teal-600 text-white font-semibold py-4 px-6 rounded-xl hover:from-vista-teal-600 hover:to-vista-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? 'Calculating...' : 'Calculate Investment'}
              </button>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-vista-silver-200">
            <h2 className="text-xl font-bold text-vista-silver-900 mb-6">Investment Results</h2>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {results && (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="bg-vista-teal-50 border border-vista-teal-200 rounded-xl p-6">
                  <h3 className="text-base font-semibold text-vista-teal-900 mb-4">Investment Summary</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Total Investment</span>
                      <span className="text-vista-teal-900 font-semibold text-base">
                        {formatCurrency(results.total_investment_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Total Return</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatCurrency(results.total_return_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Annualized Return</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatPercentage(results.annualized_return_pct)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                      <span className="text-vista-silver-700 font-medium">Cash on Cash Return</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatPercentage(results.cash_on_cash_return_pct)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2">
                      <span className="text-vista-silver-700 font-medium">Net Operating Income</span>
                      <span className="text-vista-teal-900 font-semibold">
                        {formatCurrency(results.net_operating_income_eur)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Projected Sale Price */}
                <div className="bg-vista-silver-50 border border-vista-silver-200 rounded-xl p-6">
                  <h3 className="text-base font-semibold text-vista-silver-900 mb-4">Projected Sale</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-vista-silver-100">
                      <span className="text-vista-silver-700 font-medium">Projected Sale Price</span>
                      <span className="text-vista-silver-900 font-semibold">
                        {formatCurrency(results.projected_sale_price_eur)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2">
                      <span className="text-vista-silver-700 font-medium">Appreciation Gain</span>
                      <span className="text-vista-silver-900 font-semibold">
                        {formatCurrency(results.projected_sale_price_eur - formData.property_price_eur)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cash Flow Analysis */}
                {results.cash_flow_analysis && (
                  <div className="bg-vista-teal-50 border border-vista-teal-200 rounded-xl p-6">
                    <h3 className="text-base font-semibold text-vista-teal-900 mb-4">Cash Flow Analysis</h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                        <span className="text-vista-silver-700 font-medium">Monthly Cash Flow</span>
                        <span className="text-vista-teal-900 font-semibold">
                          {formatCurrency(results.cash_flow_analysis.monthly_cash_flow_eur)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2 border-b border-vista-teal-100">
                        <span className="text-vista-silver-700 font-medium">Annual Cash Flow</span>
                        <span className="text-vista-teal-900 font-semibold">
                          {formatCurrency(results.cash_flow_analysis.annual_cash_flow_eur)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center py-2">
                        <span className="text-vista-silver-700 font-medium">Monthly Rent</span>
                        <span className="text-vista-teal-900 font-semibold">
                          {formatCurrency(results.cash_flow_analysis.monthly_rent_eur)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {results.message && (
                  <div className="bg-vista-silver-50 border border-vista-silver-200 rounded-xl p-4">
                    <p className="text-vista-silver-700">{results.message}</p>
                  </div>
                )}
              </div>
            )}

            {!results && !error && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📈</div>
                <p className="text-vista-silver-600">
                  Enter your investment parameters and click "Calculate Investment" to see the results.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;
